import useReveal from "@/hooks/useReveal";
import { Link } from "react-router-dom";
import { Heart, Users, Globe2, Sparkles } from "lucide-react";

const community =
  "https://cdn.pixabay.com/photo/2020/08/22/09/57/carpool-5508006_1280.png";

const values = [
  { icon: Heart, title: "People first", desc: "Every feature begins with a real commuter problem." },
  { icon: Users, title: "Community-owned", desc: "Trip hosts and co-travellers shape RideMate together." },
  { icon: Globe2, title: "Greener cities", desc: "Fewer cars, cleaner air, lighter wallets." },
  { icon: Sparkles, title: "Made in India", desc: "Built for Indian roads, languages, and prices." },
];

export default function AboutPage() {
  const ref = useReveal();
  return (
    <main ref={ref} data-testid="about-page" className="pt-12 bg-white">
      {/* Hero */}
      <section className="relative py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-3xl reveal">
            <span className="font-display text-[11px] uppercase tracking-[0.25em] text-[#5FA836] font-bold">
              About RideMate
            </span>
            <h1 className="mt-4 font-display text-5xl md:text-7xl font-black tracking-[-0.03em] leading-[0.95] text-[#24303F]">
              Two roles. <br />
              <span className="text-gray-400">One commute.</span>
            </h1>
            <p className="mt-7 font-body text-gray-600 text-lg max-w-2xl leading-relaxed">
              Same road. Same time. Same destination — so why not the same ride?
              RideMate brings together commuters heading the same way, making
              every journey friendlier, greener, and up to 60% lighter on the
              wallet.
            </p>
          </div>
        </div>
      </section>

      {/* Image + story */}
      <section className="relative py-12 md:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal">
            <div className="rounded-3xl overflow-hidden border border-[#e5e7eb] bg-[#F9FAFB]">
              <img
                src={community}
                alt="Friends sharing a ride"
                className="w-full h-[420px] md:h-[520px] object-contain p-6 md:p-10"
              />
            </div>
          </div>
          <div className="lg:col-span-6 reveal">
            <h2 className="font-display text-3xl md:text-5xl font-black tracking-[-0.03em] leading-tight text-[#24303F]">
              The story
            </h2>
            <div className="mt-6 space-y-5 font-body text-gray-600 text-[16px] leading-relaxed">
              <p>
                RideMate started with a simple observation: the same person who
                books a cab to office on Monday often drives that same route on
                Tuesday. Why should they need two different apps?
              </p>
              <p>
                We built RideMate as a single, dual-mode platform. Find a pool
                when you're not driving. Post a trip when you are. The 10-minute
                role-switch makes it effortless.
              </p>
              <p>
                We're a small team based in India, building for Indian commuters —
                with rupee-friendly pricing, regional language support, and a
                relentless focus on safety and trust.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative py-20 md:py-28 border-t border-[#e5e7eb] bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="reveal max-w-3xl">
            <span className="font-display text-[11px] uppercase tracking-[0.25em] text-[#5FA836] font-bold">
              What we believe
            </span>
            <h2 className="mt-4 font-display text-4xl md:text-6xl font-black tracking-[-0.03em] leading-tight text-[#24303F]">
              Values, <span className="text-gray-400">not slogans.</span>
            </h2>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <div
                key={v.title}
                data-testid={`about-value-${i}`}
                className="reveal lift rounded-3xl border border-[#e5e7eb] bg-white p-7"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="h-10 w-10 rounded-xl bg-[#5FA836]/10 grid place-items-center text-[#5FA836]">
                  <v.icon className="w-5 h-5" />
                </div>
                <h3 className="mt-5 font-display text-xl font-bold text-[#24303F]">{v.title}</h3>
                <p className="mt-2 font-body text-gray-600 text-sm leading-relaxed">
                  {v.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 border-t border-[#e5e7eb] bg-white">
        <div className="mx-auto max-w-5xl px-6 lg:px-10 text-center reveal">
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-[-0.03em] leading-tight text-[#24303F]">
            Ride with us from day one.
          </h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/"
              data-testid="about-cta-waitlist"
              className="btn-primary rounded-full px-7 py-3 font-display"
            >
              Join the waitlist
            </Link>
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="btn-ghost rounded-full px-7 py-3 font-display"
            >
              Contact the team
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
